import { JSX, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    getConversationalSearchContentClass,
    SearchFilterContentTypes,
    SearchOrigins,
} from 'search-gateway-client/components';
import { useStatsigExperimentContext } from 'statsig/components';
import { twMerge } from 'tailwind-merge';

import { Close, Search as SearchIcon } from '@videoblocks/react-icons';

import { WindowAdapter } from '../../WindowAdapter';
import { NavSearchContext, NavSearchContextProvider, NavSearchProps } from '../context/NavSearchContext';
import { useSearchWithAI } from '../hooks/useSearchWithAI';
import { CrossAssetNavSuggestions } from '../types/shared';
import getConversationalSearchUrl from '../utils/getConversationalSearchUrl';
import useMedia from '../utils/useMedia';
import { saveSearchHistory } from './AutosuggestElements/utils';
import CrossAssetNavSearchBox from './NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from './NavSearchElements/NavSearchBox';
import NavSearchButton from './NavSearchElements/NavSearchButton';
import NavSearchDropdown from './NavSearchElements/NavSearchDropdown';
import TopNavSearchToggle from './NavSearchElements/TopNavSearchToggle';
import { cleanSearchKeywords } from './NavSearchElements/searchUtils';

interface NavSearchExtraProps {
    compact?: boolean;
    onCollapse?: () => void;
    showCloseButton?: boolean;
}

// Same values as getAccessibleFocusStyles(), drawn on the whole bar while its text input has focus.
const SEARCH_INPUT_FOCUS_ON_CONTAINER =
    'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-white has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-blue-600 [&_input:focus-visible]:ring-0';

export default function NavSearch(props: NavSearchProps & NavSearchExtraProps): JSX.Element {
    return (
        <NavSearchContextProvider {...props}>
            <NavSearchForm {...props} />
        </NavSearchContextProvider>
    );
}

// Rendered inside the provider so it can read NavSearchContext (conversational
// search state) with hooks, which the outer component cannot.
function NavSearchForm(props: NavSearchProps & NavSearchExtraProps): JSX.Element {
    const {
        onSubmit,
        userHasAnySubscription,
        useSnapshots,
        searchOrigin: searchOriginFromProps = SearchOrigins.TOP_NAV,
        className,
        compact = false,
        onCollapse,
        showCloseButton = false,
    } = props;
    const formRef = useRef<HTMLFormElement>(null);

    const [contentType, setContentType] = useState(props.contentType);
    const [searchTerm, setSearchTerm] = useState('');

    const { setShowSuggestions } = useContext(NavSearchContext);
    const context = useStatsigExperimentContext();

    const { showAiCta, conversationalSearchEnabled, getPlaceholder, isSearchWithAIEnabledForContentType } =
        useSearchWithAI({
            // The toggle is a compact/top-nav-only surface; non-compact bars never show it.
            showConversationalSearchCta: compact && (context.showConversationalSearchCta ?? false),
            contentType,
        });

    const handleCloseClick = (): void => {
        if (searchTerm.trim()) {
            setSearchTerm('');
        } else {
            onCollapse?.();
        }
    };

    const makeEmptySuggestions = (): CrossAssetNavSuggestions => ({
        keywords: [],
        relatedCollections: [],
        latestCollections: [],
        trendingCollections: [],
        relatedContentTypesAndKeywords: [],
    });

    const [suggestions, setSuggestions] = useState<CrossAssetNavSuggestions>(makeEmptySuggestions());

    const clearSuggestions = useCallback(() => setSuggestions(makeEmptySuggestions()), []);

    // i.e. tailwind md breakpoint https://storywind.netlify.app/?path=/story/design-grids--page#medium-672-1024px
    const isMediumScreen = useMedia('(min-width: 672px)', true);
    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setContentType(props.contentType);
    }, [props.contentType]);

    const handleDropdownChange = (contentType: string): void => {
        setContentType(contentType as SearchFilterContentTypes);
    };

    const aiSupportedForContentType = isSearchWithAIEnabledForContentType(contentType);
    // The toggle's no-AI state is itself a submit circle, so the bar's own
    // trailing submit button would duplicate the magnifying-glass affordance.
    const showNoAiSubmitCircle = showAiCta && !aiSupportedForContentType;
    // Guarded by the synchronous support check, not only the enabled flag: the
    // auto-disable effect lags a content-type change by one flush, which would
    // otherwise flash AI copy on an unsupported type for a frame.
    const placeholder = showAiCta && aiSupportedForContentType ? getPlaceholder(undefined, contentType) : undefined;

    const handleSubmit = (
        contentTypeSearched: SearchFilterContentTypes = contentType,
        value: string = searchTerm,
        searchOriginOverride?: SearchOrigins,
    ): void => {
        const cleanedSearchKeywords = cleanSearchKeywords(value);
        const searchOrigin = searchOriginOverride ?? searchOriginFromProps;
        // Re-verify support for the *submitted* content type synchronously:
        // suggestion clicks can submit a different content type than the dropdown
        // shows, and the enabled flag lags content-type changes by one flush.
        if (showAiCta && conversationalSearchEnabled && isSearchWithAIEnabledForContentType(contentTypeSearched)) {
            WindowAdapter.setLocation(
                getConversationalSearchUrl(
                    cleanedSearchKeywords,
                    searchOrigin,
                    getConversationalSearchContentClass(contentTypeSearched),
                ),
            );
            return;
        }
        if (context?.isSearchHistoryEnabled) {
            saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
        }
        onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
    };

    return (
        <div className={`flex flex-col xl:w-full ${className ?? 'lg:w-72'} font-inter`}>
            <form
                ref={formRef}
                className={twMerge(
                    compact
                        ? 'w-full max-w-[1034px] mx-auto h-12 px-2 bg-white rounded-full border border-solid border-gray-400 justify-between items-center inline-flex'
                        : 'w-full h-10 px-4 py-2 bg-white rounded-3xl border border-solid border-gray-500 justify-between items-center inline-flex',
                    SEARCH_INPUT_FOCUS_ON_CONTAINER,
                )}
                onSubmit={(event) => {
                    event.preventDefault();
                    setShowSuggestions(false);
                    handleSubmit(contentType, searchTerm);
                }}
            >
                <div
                    className={
                        compact
                            ? 'w-full h-8 justify-start items-center gap-2 inline-flex'
                            : 'w-full h-6 justify-start items-center gap-0.5 inline-flex'
                    }
                >
                    {showAiCta && <TopNavSearchToggle disabledForContentType={!aiSupportedForContentType} />}
                    <NavSearchDropdown
                        onChange={handleDropdownChange}
                        value={contentType}
                        useSnapshots={useSnapshots}
                        compact={compact}
                        bareText={showAiCta}
                    />
                    {!compact && <div className="xl:w-px lg:w-px md:w-px h-6 bg-gray-500" />}
                    {isMediumScreen ? (
                        <CrossAssetNavSearchBox
                            contentType={contentType}
                            setSearchTerm={setSearchTerm}
                            onSubmit={handleSubmit}
                            searchTerm={searchTerm}
                            suggestions={suggestions}
                            setSuggestions={setSuggestions}
                            clearSuggestions={clearSuggestions}
                            placeholder={placeholder}
                        />
                    ) : (
                        <NavSearchBox
                            contentType={contentType}
                            onChange={setSearchTerm}
                            onSubmit={handleSubmit}
                            searchTerm={searchTerm}
                            userHasAnySubscription={userHasAnySubscription}
                            placeholder={placeholder}
                        />
                    )}
                </div>
                {compact && showCloseButton && (
                    <button
                        type="button"
                        onClick={handleCloseClick}
                        aria-label="Clear or close search"
                        className="flex-shrink-0 p-2 mr-1 text-gray-500 hover:text-gray-700"
                    >
                        <Close className="w-4 h-4 fill-current" />
                    </button>
                )}
                {compact && !showCloseButton && !showNoAiSubmitCircle && (
                    <button
                        type="submit"
                        aria-label="Submit search"
                        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-gray-900 hover:bg-black text-white"
                    >
                        <SearchIcon className="w-4 h-4 fill-current" />
                    </button>
                )}
                {!compact && <NavSearchButton />}
            </form>
        </div>
    );
}
